import { FormikProps } from "formik";
import { FC } from "react";
import { WorksCategoriesForm } from "../../../../../domain/models/dto/WorksCategoriesDTO";
import { useTranslation } from "react-i18next";
import SortArrowsIconSVG from "../../../svg/functional/SortArrowsIconSVG";
import CurrencyIconSVG from "../../../svg/CurrencyIconSVG";
import TargetIconSVG from "../../../svg/TargetIconSVG";
import ClockIconSVG from "../../../svg/ClockIconSVG";
import CustomCheckbox from "../../../../../custom-components/form-inputs/CustomCheckbox";
import CheckMarkIconSVG from "../../../svg/CheckMarkIconSVG";
import useScreenSize from "../../../../../custom-hooks/base-hooks/useScreenSize";
import WorkCategoriesPopup from "../../popups/WorkCategoriesPopup";
import ClosingCrossLightGrey from "../../../svg/functional/ClosingCrossLightGreySVG";
import { WorkSearchSortForm } from "../../../../../domain/models/dto/WorkDTO";
import WorkSortingTooltip from "./WorkSortingTooltip";
import { WorkSortingFields } from "../../../../../domain/enums/entity-fields/WorkSortingFields";
import WorkSortingPopup from "./WorkSortingPopup";

interface CatalogSortMobileProps {
    form: FormikProps<WorkSearchSortForm>;
    disableInactiveCheckbox?: boolean;
    showNonActiveCheckboxHandler: () => void;
    workCategoriesHandler: (values: WorksCategoriesForm[]) => void;
    unselectCategoryHandler: (value: WorksCategoriesForm) => void;
    onOpenWorkCategoriesPopup: () => void;
    resetFormCallback: () => Promise<void>;
    remainingCategories: number;
    sortingType: WorkSortingFields;
    setSortingType: (value: WorkSortingFields) => void;
    openingHandler: (value: WorkSortingFields) => void;
}

const CatalogSortMobile: FC<CatalogSortMobileProps> = (props) => {
    const { t } = useTranslation();
    const { upTo650, moreOrEqual650 } = useScreenSize();

    return (
        <div className="relative flex flex-col w-full">
            <div className="flex items-center mb-5">
                <SortArrowsIconSVG className="text-textFaded mr-2.5" />
                <span className="H20B dmax2:H16B text-textFaded mr-11 dmax2:mr-5">{t("catalog.sort.label")}</span>
            </div>
            <div className="flex justify-between mb-5">
                {moreOrEqual650 && (
                    <div className="flex gap-3">
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.PRICE}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <CurrencyIconSVG className="text-textFaded group-hover:text-textWhite mr-2 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2">
                                        {t(`catalog.sort.${WorkSortingFields.PRICE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.DISTANCE}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <TargetIconSVG className="text-textFaded group-hover:text-textWhite mr-2 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2">
                                        {t(`catalog.sort.${WorkSortingFields.DISTANCE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingTooltip
                            form={props.form}
                            sortingType={WorkSortingFields.START_FROM}
                            currentSortingType={props.sortingType}
                            openingHandler={props.openingHandler}
                            setSortingType={props.setSortingType}
                            buttonElement={
                                <>
                                    <ClockIconSVG className="text-textFaded group-hover:text-textWhite mr-2 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2 tmax4:mr-0">
                                        {t(`catalog.sort.${WorkSortingFields.START_FROM}`)}
                                    </span>
                                </>
                            }
                        />
                    </div>
                )}
                {upTo650 && (
                    <>
                        <WorkSortingPopup
                            form={props.form}
                            sortingType={WorkSortingFields.PRICE}
                            currentSortingType={props.sortingType}
                            setSortingType={props.setSortingType}
                            openingHandler={props.openingHandler}
                            buttonElement={
                                <>
                                    <CurrencyIconSVG className="text-textFaded group-hover:text-textWhite mr-2 tmax4:mr-1 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2">
                                        {t(`catalog.sort.${WorkSortingFields.PRICE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingPopup
                            form={props.form}
                            sortingType={WorkSortingFields.DISTANCE}
                            currentSortingType={props.sortingType}
                            setSortingType={props.setSortingType}
                            openingHandler={props.openingHandler}
                            buttonElement={
                                <>
                                    <TargetIconSVG className="text-textFaded group-hover:text-textWhite mr-2 tmax4:mr-1 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2">
                                        {t(`catalog.sort.${WorkSortingFields.DISTANCE}`)}
                                    </span>
                                </>
                            }
                        />
                        <WorkSortingPopup
                            form={props.form}
                            sortingType={WorkSortingFields.START_FROM}
                            currentSortingType={props.sortingType}
                            setSortingType={props.setSortingType}
                            openingHandler={props.openingHandler}
                            buttonElement={
                                <>
                                    <ClockIconSVG className="text-textFaded group-hover:text-textWhite mr-2 tmax4:mr-1 w-8 dmax2:w-6 h-8 dmax2:h-6 transition duration-300" />
                                    <span className="H20 dmax2:H16 text-textFaded group-hover:text-textWhite transition duration-300 mr-2">
                                        {t(`catalog.sort.${WorkSortingFields.START_FROM}`)}
                                    </span>
                                </>
                            }
                        />
                    </>
                )}
                {!props.disableInactiveCheckbox && moreOrEqual650 && (
                    <div className="relative flex items-center">
                        <CustomCheckbox
                            labelKey="catalog.sort.showInactive"
                            checked={props.form.values.showInactive}
                            onClick={props.showNonActiveCheckboxHandler}
                            labelClassName="H16 td4:H14B text-textFaded td5:w-[160px] ti6:w-[90px] ti7:w-[90px]"
                            checkboxClassName="flex justify-center items-center min-w-6 min-h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200 ml-2"
                            checkboxCheckedClassName="!border-0 bg-textWarning"
                            wrapClassName="flex flex-row-reverse items-center"
                            additionalLayoutElement={<CheckMarkIconSVG />}
                        />
                    </div>
                )}
            </div>
            <div className="relative tmax4:w-full">
                <div className="relative flex flex-wrap items-center tmax4:w-full gap-y-3">
                    <WorkCategoriesPopup
                        defaultSelected={props.form.values.categories}
                        confirmButtonTitle={t("workCreation.categoryConfirmation")}
                        confirmButtonClassName="w-[315px] dmax2:w-[225px] h-[50px] dmax2:h-[40px] mt-8 dmax2:mt-6"
                        buttonElement={
                            <div className="button dmin2:H16B dmax2:H14B dmin2:w-[225px] td4:w-[175px] tmax4:w-full dmin2:h-[50px] dmax2:h-[40px]">
                                {t("workCreation.fields.categoryButtonLabel")}
                            </div>
                        }
                        buttonElementClassName="relative tmax4:w-full mr-3"
                        confirmHandler={props.workCategoriesHandler}
                        onopenPopupHandler={props.onOpenWorkCategoriesPopup}
                    />
                    {props.form.values.categories.slice(0, 2).map((item, index) => (
                        <div key={`chosen-category-${index}`} className="mr-3">
                            <div className="relative flex items-center justify-between border border-textFaded rounded-xl pt-2 dmax2:pt-[6px] pb-[10px] dmax2:pb-2 pl-4 dmax2:pl-3 pr-[40px] dmax2:pr-[34px]">
                                <span className="H16 dmax2:H14 text-textWhite">
                                    {t(`work.categories.${item.category}`)}
                                </span>
                                <ClosingCrossLightGrey
                                    onClick={() => props.unselectCategoryHandler(item)}
                                    className="absolute right-4 dmax2:right-3 cursor-pointer"
                                />
                            </div>
                        </div>
                    ))}
                    {props.form.values.categories.slice(0, 2)?.length < props.form.values.categories?.length && (
                        <span className="H16B text-textFaded mr-3">
                            {t("workCreation.fields.categoriesOverflowLabel", { value: props.remainingCategories })}
                        </span>
                    )}
                </div>
            </div>
            {moreOrEqual650 && (
                <div
                    onClick={props.resetFormCallback}
                    className="cursor-pointer H20B dmax2:H14B text-textFaded text-end mt-4"
                >
                    {t("catalog.sort.clear")}
                </div>
            )}
            {upTo650 && (
                <>
                    <div className="relative flex items-center">
                        <CustomCheckbox
                            labelKey="catalog.sort.showInactive"
                            checked={props.form.values.showInactive}
                            onClick={props.showNonActiveCheckboxHandler}
                            labelClassName="H16 td4:H14B text-textFaded ml-2"
                            checkboxClassName="flex justify-center items-center min-w-6 min-h-6 border-2 border-textFaded rounded-sm cursor-pointer transition duration-200"
                            checkboxCheckedClassName="!border-0 bg-textWarning"
                            wrapClassName="flex items-center tmax4:my-5"
                            additionalLayoutElement={<CheckMarkIconSVG />}
                        />
                    </div>
                    <div
                        onClick={props.resetFormCallback}
                        className="cursor-pointer H20B dmax2:H14B text-textFaded text-end"
                    >
                        {t("catalog.sort.clear")}
                    </div>
                </>
            )}
        </div>
    );
};

export default CatalogSortMobile;
